import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowRight, Download } from 'lucide-react';
import { categories, products } from '../data/mock';

const CategoryPage = () => {
  const { slug } = useParams();
  const category = categories.find(cat => cat.slug === slug);
  const categoryProducts = products.filter(product => product.category === slug);

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">Category Not Found</h1>
          <Link to="/" className="text-slate-600 hover:text-slate-900">
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Category Header */}
      <section 
        className="relative h-80 bg-slate-900 overflow-hidden"
        style={{
          backgroundImage: `url('${category.image}')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-slate-900/80"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
          <div className="max-w-3xl">
            <h1 className="text-5xl font-bold text-white mb-4">
              {category.name}
            </h1>
            <p className="text-xl text-slate-200 leading-relaxed">
              {category.description}
            </p>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10">
            <h2 className="text-3xl font-bold text-slate-900 mb-2">
              Products in {category.name}
            </h2>
            <p className="text-slate-600">
              {categoryProducts.length} professional-grade solutions
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {categoryProducts.map((product) => (
              <div
                key={product.id}
                className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden group"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img 
                    src={product.image} 
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-slate-900 mb-3">
                    {product.name}
                  </h3>
                  <p className="text-slate-600 mb-4 leading-relaxed">
                    {product.shortDescription}
                  </p>
                  
                  {/* Key Properties */}
                  <div className="mb-6">
                    <h4 className="font-semibold text-slate-900 mb-2">Key Properties:</h4>
                    <ul className="space-y-1">
                      {product.properties.slice(0, 3).map((prop, index) => (
                        <li key={index} className="text-sm text-slate-600 flex items-start">
                          <span className="text-slate-400 mr-2">•</span>
                          {prop}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Advantages */}
                  <div className="mb-6">
                    <h4 className="font-semibold text-slate-900 mb-2">Advantages:</h4>
                    <ul className="space-y-1">
                      {product.advantages.slice(0, 2).map((adv, index) => (
                        <li key={index} className="text-sm text-slate-600 flex items-start">
                          <span className="text-slate-400 mr-2">•</span>
                          {adv}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Sustainability Badge */}
                  {product.sustainability && (
                    <div className="mb-6 p-3 bg-green-50 rounded-lg border border-green-200">
                      <p className="text-sm text-green-800 font-medium">
                        {product.sustainability}
                      </p>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex flex-col space-y-3">
                    <Link
                      to={`/product/${product.id}`}
                      className="w-full inline-flex items-center justify-center px-6 py-3 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors font-semibold group"
                    >
                      View Product Details
                      <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                    <button
                      className="w-full inline-flex items-center justify-center px-6 py-3 bg-white border-2 border-slate-900 text-slate-900 rounded-lg hover:bg-slate-50 transition-colors font-semibold"
                      onClick={() => window.open(product.technicalSheet, '_blank')}
                    >
                      <Download className="mr-2 w-4 h-4" />
                      Download TDS
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">
            Need More Information?
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            Contact us for product information and assistance with selecting the right products for your project requirements.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link 
              to="/request-quote" 
              className="inline-flex items-center px-8 py-4 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold text-lg"
            >
              Request Quote
            </Link>
            <Link 
              to="/contact" 
              className="inline-flex items-center px-8 py-4 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors font-semibold text-lg group"
            >
              Contact Us
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CategoryPage;
